/**
 * api-client.ts — typed wrappers for every backend endpoint the CLI hits.
 *
 * Phase CG. Each function returns a structured result with `ok`, `status`,
 * `data`, and `error`. Bearer key (zsm_/zeq_ak_) is read from
 * window.Zeq.identity when needed; same-origin fetch handles session
 * cookies for owner-gated routes.
 *
 * If a backend endpoint is missing or unreachable, callers receive
 * `{ ok: false, status: 0, error: "..." }` and surface `(endpoint pending)`
 * notice rather than throwing — documented degradation.
 */

declare global {
  interface Window {
    Zeq?: {
      identity?: {
        snapshot?: () => { zid?: string; slug?: string; tier?: string; apiKey?: string; publishKey?: string; email?: string };
        ready?: () => Promise<{ zid?: string; slug?: string; tier?: string; apiKey?: string; publishKey?: string; email?: string }>;
        apiKey?: string;
        publishKey?: string;
        slug?: string;
        zid?: string;
      };
    };
  }
}

import { getSlug } from "../agent/agent-rail-bridge.js";

export interface Result<T = unknown> {
  ok: boolean;
  status: number;
  data?: T;
  error?: string;
}

function authHeaders(): HeadersInit {
  // BUILD-G — if a CLI profile is active, its key overrides the page identity.
  // Set by `profile use <name>`; cleared by `profile clear`. Same-origin only
  // (cross-origin forks are the native CLI's job).
  try {
    if (typeof localStorage !== "undefined") {
      const pk = localStorage.getItem("zeq.cli.profile.key");
      if (pk) return { "Content-Type": "application/json", "Authorization": "Bearer " + pk, "X-Zeq-Api-Key": pk };
    }
  } catch { /* no localStorage (SSR / native) — fall through to page identity */ }
  const id = (typeof window !== "undefined" && window.Zeq && window.Zeq.identity) || null;
  // P21.R6 CONNECT — resolve THIS machine's keys through the one
  // resolver (Zeq.identity.credsFor). The terminal is bound to the
  // page's machine; the active account's keys may belong to a different
  // machine entirely (that was the "Invalid or expired ZeqAuth token"
  // bug: a zsm_ admin key — or a stale session — sent to the zeq_ak_
  // compute surface). publishKey (zeq_ak_) is the /api/zeq/* credential;
  // apiKey (zsm_) is the fallback only for surfaces that take it.
  let publishKey = "";
  let adminKey = "";
  try {
    const anyId = id as { credsFor?: (s: string) => { publishKey?: string | null; apiKey?: string | null } } | null;
    if (anyId && typeof anyId.credsFor === "function") {
      const creds = anyId.credsFor(getSlug() ?? "");
      publishKey = (creds && creds.publishKey) || "";
      adminKey = (creds && creds.apiKey) || "";
    }
  } catch { /* resolver not ready */ }
  if (!publishKey && !adminKey) {
    // Legacy fallback — old identity builds without credsFor.
    let snap: Record<string, unknown> | null = null;
    try {
      const anyId = id as { snapshot?: () => Record<string, unknown>; get?: () => Record<string, unknown> } | null;
      if (anyId && typeof anyId.snapshot === "function") snap = anyId.snapshot();
      else if (anyId && typeof anyId.get === "function") snap = anyId.get();
    } catch { /* */ }
    publishKey = ((snap && (snap.publishKey as string | undefined)) || (id && (id as { publishKey?: string }).publishKey) || "") as string;
    adminKey = ((snap && (snap.apiKey as string | undefined)) || (id && (id as { apiKey?: string }).apiKey) || "") as string;
  }
  const apiKey = publishKey || adminKey;
  const h: Record<string, string> = { "Content-Type": "application/json" };
  if (apiKey) {
    h["Authorization"] = "Bearer " + apiKey;
    h["X-Zeq-Api-Key"] = apiKey;
  }
  return h;
}

async function call<T = unknown>(
  method: "GET" | "POST" | "PUT" | "PATCH" | "DELETE",
  url: string,
  body?: unknown,
  opts: { cookieOnly?: boolean } = {},
): Promise<Result<T>> {
  try {
    // cookieOnly: owner-gated SESSION routes (requireAuth) verify the
    // `zeq_auth_token` cookie. requireAuth.extractRawToken prefers the
    // Authorization header, so sending our compute key (zeq_ak_/zsm_) as
    // Bearer makes it 401 "Invalid or expired session" before it ever
    // reaches the cookie. Omit Authorization on these routes and let the
    // same-origin session cookie authenticate. (P21.R14j)
    const init: RequestInit = {
      method,
      credentials: "same-origin",
      // cookieOnly routes authenticate via the browser session cookie. The
      // native CLI has no cookie — it carries the machine's admin key — so in
      // native mode we send the Bearer even on cookieOnly routes. The server
      // now accepts a machine admin key on these owner-gated routes (it adopts
      // the machine's ownerZid, scoped to that one machine).
      headers: (opts.cookieOnly && !(globalThis as unknown as { __ZEQ_NATIVE__?: boolean }).__ZEQ_NATIVE__)
        ? { "Content-Type": "application/json" }
        : authHeaders(),
    };
    if (body != null && method !== "GET") {
      init.body = typeof body === "string" ? body : JSON.stringify(body);
    }
    const r = await fetch(url, init);
    let data: unknown = null;
    const ct = r.headers.get("content-type") || "";
    if (ct.indexOf("application/json") >= 0) {
      try { data = await r.json(); } catch { data = null; }
    } else {
      try { data = await r.text(); } catch { data = null; }
    }
    if (!r.ok) {
      // Row-12 audit: error can be an OBJECT ({message}) — the old read
      // surfaced "[object Object]" in every failing command.
      const rawErr = data && typeof data === "object" ? (data as { error?: unknown }).error : undefined;
      const errMsg =
        typeof rawErr === "string" ? rawErr
        : rawErr && typeof rawErr === "object" && typeof (rawErr as { message?: string }).message === "string"
          ? (rawErr as { message: string }).message
        : rawErr ? JSON.stringify(rawErr).slice(0, 300)
        : "HTTP " + r.status;
      return { ok: false, status: r.status, data: data as T, error: errMsg };
    }
    return { ok: true, status: r.status, data: data as T };
  } catch (e) {
    return { ok: false, status: 0, error: (e as Error)?.message || String(e) };
  }
}

// ── System / pulse / state ──────────────────────────────────────────────
export const Api = {
  health:           () => call("GET", "/api/health"),
  pulse:            () => call("GET", "/api/zeq/pulse"),
  phase:            () => call("GET", "/api/zeq/phase"),
  stateLive:        () => call("GET", "/api/zeq/state/live"),
  stateMachines:    (mine = false) => call("GET", "/api/chain/state-machines" + (mine ? "?mine=true" : "")),

  // Operators
  operatorsList:    (opts: { limit?: number; domain?: string; search?: string } = {}) => {
    const q = new URLSearchParams();
    if (opts.limit) q.set("limit", String(opts.limit));
    if (opts.domain) q.set("category", opts.domain);
    if (opts.search) q.set("search", opts.search);
    const s = q.toString();
    return call("GET", "/api/operators" + (s ? "?" + s : ""));
  },
  operatorsCategories: () => call("GET", "/api/operators/categories"),

  // Compute
  /** Row-12: generic escape hatch for new commands (verify, attest). */
  raw:              (method: string, path: string, body?: unknown) =>
    call(method, path, body),
  compute:          (operators: string[], inputs: Record<string, number>, domain?: string) =>
    call("POST", "/api/zeq/compute", { operators, inputs, ...(domain ? { domain } : {}) }),
  // Cost-only preview (BUILD-D). Public read — GET /api/tally/cost. No
  // execution, no charge, no envelope mint; returns {cost,price_usd,domains,…}.
  computeCost:      (operators: string[]) =>
    call("GET", "/api/tally/cost?operators=" + encodeURIComponent(operators.join(","))),

  // Chain / audit / contracts
  audit:            (slug: string, opts: { limit?: number; public?: boolean } = {}) => {
    const q = new URLSearchParams();
    if (opts.limit) q.set("limit", String(opts.limit));
    if (opts.public) q.set("public", "1");
    const s = q.toString();
    return call("GET", "/api/chain/" + encodeURIComponent(slug) + "/audit" + (s ? "?" + s : ""));
  },
  // Entangled-state walk. optionalAuth + canRead — cookieOnly so the owner's
  // session authenticates (the compute-key Bearer isn't a session token).
  // NOTE: the old `audit` wrapper pointed at /chain/<slug>/audit which is not
  // a route (404); /explore is the canonical entangled-state endpoint.
  chainExplore:     (slug: string, limit = 20) =>
    call("GET", "/api/chain/" + encodeURIComponent(slug) + "/explore?limit=" + limit, undefined, { cookieOnly: true }),
  contractsList:    (slug: string) => call("GET", "/api/chain/" + encodeURIComponent(slug) + "/contracts"),
  contractGet:      (slug: string, id: string) => call("GET", "/api/chain/" + encodeURIComponent(slug) + "/contracts/" + encodeURIComponent(id)),
  contractFireNow:  (slug: string, id: string) => call("POST", "/api/chain/" + encodeURIComponent(slug) + "/contracts/" + encodeURIComponent(id) + "/fire-now"),
  contractPause:    (slug: string, id: string) => call("POST", "/api/chain/" + encodeURIComponent(slug) + "/contracts/" + encodeURIComponent(id) + "/pause"),
  contractResume:   (slug: string, id: string) => call("POST", "/api/chain/" + encodeURIComponent(slug) + "/contracts/" + encodeURIComponent(id) + "/resume"),
  contractDryRun:   (slug: string, id: string, body: Record<string, unknown> = {}) => call("POST", "/api/chain/" + encodeURIComponent(slug) + "/contracts/" + encodeURIComponent(id) + "/dry-run", body),
  contractPreview:  (slug: string, id: string) => call("GET", "/api/chain/" + encodeURIComponent(slug) + "/contracts/" + encodeURIComponent(id) + "/preview"),
  contractNextFires:(slug: string, id: string, n = 5) => call("GET", "/api/chain/" + encodeURIComponent(slug) + "/contracts/" + encodeURIComponent(id) + "/next-fires?n=" + n),
  contractExport:   (slug: string, id: string) => call("GET", "/api/chain/" + encodeURIComponent(slug) + "/contracts/" + encodeURIComponent(id) + "/export"),
  contractImport:   (slug: string, def: Record<string, unknown>) => call("POST", "/api/chain/" + encodeURIComponent(slug) + "/contracts/import", def),
  contractDeploy:   (slug: string, def: Record<string, unknown>) => call("POST", "/api/chain/" + encodeURIComponent(slug) + "/contracts", def),
  events:           (slug: string, tag?: string) =>
    call("GET", "/api/chain/" + encodeURIComponent(slug) + "/events" + (tag ? "?tag=" + encodeURIComponent(tag) : "")),

  // Agents
  agentList:        (slug: string) => call("GET", "/api/zeq/agent/list?slug=" + encodeURIComponent(slug)),
  agentSpawn:       (directive: string, credentialId?: string) =>
    call("POST", "/api/zeq/agent/spawn", { directive, ...(credentialId ? { model_credential_id: credentialId } : {}) }),
  agentTick:        (id: string) => call("POST", "/api/zeq/agent/tick/" + encodeURIComponent(id)),
  agentKill:        (id: string) => call("POST", "/api/zeq/agent/kill/" + encodeURIComponent(id)),
  agentMesh:        (masterId: string) => call("GET", "/api/zeq/agent/mesh/" + encodeURIComponent(masterId)),

  // Skills
  skillsList:       (slug: string) => call("GET", "/api/zeq/agent/skills?slug=" + encodeURIComponent(slug)),
  skillsSynthesise: (intent: string) => call("POST", "/api/zeq/agent/skills/synthesise", { intent }),

  // Transparency / economy
  // P21.R14h — the old `walletBalance` hit `/tally/balance?slug=` which is
  // not a real route (404 → "credits fetch failed"). The canonical economy
  // reads are the owner-gated per-ZID endpoints in tallyProtocol.ts; both
  // require requireAuth + requireParamZidOwner, satisfied by the same-origin
  // session cookie (the path ZID must equal the caller's ZID).
  transparencyNow:  () => call("GET", "/api/transparency/now"),
  transparencyHist: (limit = 10) => call("GET", "/api/transparency/history?limit=" + limit),
  //   /account/:zid → { balance (ZEQ across machines), tokens_minted, plan, … }
  accountBalance:   (zid: string) => call("GET", "/api/tally/account/" + encodeURIComponent(zid), undefined, { cookieOnly: true }),
  //   /credits/:zid → { free_credits (fuel), paid_credits, envelopes{count,your_share_total}, tier, daily_rate, … }
  creditsBalance:   (zid: string) => call("GET", "/api/tally/credits/" + encodeURIComponent(zid), undefined, { cookieOnly: true }),
  tallySupply:      (slug: string) => call("GET", "/api/chain/" + encodeURIComponent(slug) + "/tally/supply"),
  //   /envelopes/:zid → { envelopes:[{token_id,user_share_zqt,…}] } — coins you hold.
  envelopesList:    (zid: string) => call("GET", "/api/tally/envelopes/" + encodeURIComponent(zid), undefined, { cookieOnly: true }),
  //   POST /envelopes/transfer — reassigns whole-envelope ownership. visibility
  //   "public" publishes the transition to the hosting domain's observer.
  //   OMIT visibility → the server applies the sender machine's publish-default
  //   (Phase 2c); explicit value always wins.
  envelopeTransfer: (tokenId: string, toZid: string, visibility?: "private" | "public") =>
                      call("POST", "/api/tally/envelopes/transfer", { token_id: tokenId, to: toZid, ...(visibility ? { visibility } : {}) }),
  //   per-machine publish default (Phase 2c)
  publishDefaultGet: () => call("GET", "/api/tally/settings/publish-default"),
  publishDefaultSet: (pub: boolean) => call("POST", "/api/tally/settings/publish-default", { public: pub }),
  //   P4.3 cross-domain claims — coins held locally but homed on a peer chain.
  claimsList:       (zid: string) => call("GET", "/api/tally/claims/" + encodeURIComponent(zid)),
  //   onward-transfer a held claim via the local proxy (server routes to the home chain).
  claimOnward:      (tokenId: string, toZid: string, toOrigin?: string) =>
                      call("POST", "/api/tally/claims/transfer", { token_id: tokenId, to: toZid, ...(toOrigin ? { to_origin: toOrigin } : {}) }),
  //   P4.4 publish a held claim's receipt to this domain's observer (anchored to home).
  claimPublish:     (tokenId: string) => call("POST", "/api/tally/claims/publish", { token_id: tokenId }),

  // External APIs (Phase G)
  apisList:         (slug: string) => call("GET", "/api/zeq/apis/" + encodeURIComponent(slug)),
  apisAdd:          (slug: string, def: Record<string, unknown>) =>
    call("POST", "/api/zeq/apis/" + encodeURIComponent(slug), def),
  apisTest:         (slug: string, id: string) =>
    call("POST", "/api/zeq/apis/" + encodeURIComponent(slug) + "/" + encodeURIComponent(id) + "/test"),
  apisRemove:       (slug: string, id: string) =>
    call("DELETE", "/api/zeq/apis/" + encodeURIComponent(slug) + "/" + encodeURIComponent(id)),

  // PRD
  // PRD lives at /api/zeq/agent/prd/:slug (NOT /api/zeq/prd) and the write
  // body is { markdown }, not { content }. Owner-gated → cookieOnly. (P21.R21b)
  prdGet:           (slug: string) => call("GET", "/api/zeq/agent/prd/" + encodeURIComponent(slug), undefined, { cookieOnly: true }),
  prdPut:           (slug: string, content: string) => call("PUT", "/api/zeq/agent/prd/" + encodeURIComponent(slug), { markdown: content }, { cookieOnly: true }),

  // Site hosting — published pages for a machine. Public read; the old
  // /api/chain/<slug>/site/list path never existed (the chain guard 401'd it,
  // which the CLI mislabelled "(endpoint pending)"). Canonical route is the
  // public pages list in stateMachinePages.ts. (P21.R15-B)
  siteList:         (slug: string) => call("GET", "/api/state-machines/" + encodeURIComponent(slug) + "/pages"),
  // Site hosting write-path (BUILD-E). Owner-gated (requireSessionOrMachineKey
  // + requireOwnerOrAdmin) → cookieOnly so the session cookie authenticates
  // (the compute-key Bearer would 401 here). Mirrors the Workbench step-7
  // deploy: POST …/pages/:page/publish with { title, html, agentZid }.
  siteGet:          (slug: string, pageSlug: string) =>
    call("GET", "/api/state-machines/" + encodeURIComponent(slug) + "/pages/" + encodeURIComponent(pageSlug)),
  sitePublish:      (slug: string, pageSlug: string, body: { title: string; html: string; pythonScript?: string; agentZid: string }) =>
    call("POST", "/api/state-machines/" + encodeURIComponent(slug) + "/pages/" + encodeURIComponent(pageSlug) + "/publish", body, { cookieOnly: true }),
  siteUnpublish:    (slug: string, pageSlug: string) =>
    call("POST", "/api/state-machines/" + encodeURIComponent(slug) + "/pages/" + encodeURIComponent(pageSlug) + "/unpublish", undefined, { cookieOnly: true }),

  // Machine developer API keys (BUILD-F) — the zeq_ak_ credential an app/native
  // CLI uses as `Authorization: Bearer …` for headless compute. Owner-gated
  // (requireAuth + admin role) → cookieOnly so the session authenticates. The
  // raw key is returned ONCE by mint and never re-fetchable.
  // Genesis / mint a new state machine (BUILD-I). register-v3 receives an
  // ALREADY client-hashed equation (HITE HMAC) — the server never sees the raw
  // equation. Public endpoint (creates a fresh ZID + auto state machine).
  registerV3:       (body: { equation_hash: string; equation_salt: string; display_name?: string | null; hash_version: number }) =>
    call("POST", "/api/zeq-auth/register-v3", body),
  // Row 12.b — full account lifecycle from the terminal.
  // auth-bootstrap: the 7-step wizard derives an account equation from a
  // free-form query (≥4 meaningful tokens). Dedicated rate bucket — account
  // creation can't be starved by general anon compute traffic.
  authBootstrap:    (query: string) =>
    call("POST", "/api/zeq/wizard/auth-bootstrap", { query }),
  // salt/:zid: public, decoy-on-miss (enumeration-safe). hash_version 2 = HITE.
  saltFor:          (zid: string) =>
    call("GET", "/api/zeq-auth/salt/" + encodeURIComponent(zid)),
  // login-v3: server only ever sees the candidate HMAC, never the equation.
  loginV3:          (zid: string, candidate_hash: string) =>
    call("POST", "/api/zeq-auth/login-v3", { zid, candidate_hash }),
  keysList:         (slug: string) =>
    call("GET", "/api/chain/" + encodeURIComponent(slug) + "/api-keys", undefined, { cookieOnly: true }),
  keysMint:         (slug: string, name: string, scope: string) =>
    call("POST", "/api/chain/" + encodeURIComponent(slug) + "/api-keys", { name, scope }, { cookieOnly: true }),
  keysRevoke:       (slug: string, id: string) =>
    call("DELETE", "/api/chain/" + encodeURIComponent(slug) + "/api-keys/" + encodeURIComponent(id), undefined, { cookieOnly: true }),

  // ── ZSC (Zeq Secure Context) — Phase Ω4 ─────────────────────────────────
  // Replaces .env for production secrets. Every read is chain-audited.
  // All endpoints require admin cookie (requireAdmin middleware).
  zscList:          () => call("GET", "/api/zsc/list"),
  zscInfo:          (name: string) => call("GET", "/api/zsc/info/" + encodeURIComponent(name)),
  zscSet:           (
    name: string,
    value: string,
    opts: { purposeTag?: string; boundZid?: string; permissions?: string[]; expiresZeqond?: number } = {},
  ) => call("POST", "/api/zsc/set", { name, value, ...opts }),
  zscRotate:        (name: string) => call("POST", "/api/zsc/rotate/" + encodeURIComponent(name)),
  zscGrant:         (name: string, zid: string) => call("POST", "/api/zsc/grant/" + encodeURIComponent(name), { zid }),
  zscRevoke:        (name: string, zid: string) => call("POST", "/api/zsc/revoke/" + encodeURIComponent(name), { zid }),
  zscAudit:         (name: string, limit = 50) => call("GET", "/api/zsc/audit/" + encodeURIComponent(name) + "?limit=" + limit),
  zscDelete:        (name: string) => call("DELETE", "/api/zsc/" + encodeURIComponent(name)),
};

/** Identity snapshot — safe at any time. */
export function getIdentity(): {
  zid?: string;
  slug?: string;
  tier?: string;
  apiKey?: string;
  publishKey?: string;
  email?: string;
} {
  try {
    const id = window.Zeq && window.Zeq.identity;
    if (!id) {
      const s1 = getSlug();
      return s1 ? { slug: s1, zid: s1.toUpperCase() } : {};
    }
    const anyId = id as { snapshot?: () => Record<string, string | undefined>; get?: () => Record<string, string | undefined> };
    let snap: Record<string, string | undefined> | null = null;
    if (typeof anyId.snapshot === "function") snap = anyId.snapshot() ?? null;
    else if (typeof anyId.get === "function") snap = anyId.get() ?? null;
    let out: { zid?: string; slug?: string; tier?: string; apiKey?: string; publishKey?: string; email?: string };
    if (snap) {
      const plan = snap.plan as string | undefined;
      out = { ...snap, tier: snap.tier ?? plan };
    } else {
      out = {
        apiKey: id.apiKey,
        publishKey: id.publishKey,
        slug: id.slug,
        zid: id.zid,
      };
    }
    // P21.R8 — standalone surfaces (e.g. /apps/zeq-cli/) may run without
    // the identity script or before it hydrates. The slug resolver reads
    // the same stores (active account → legacy slot); the ZID is the
    // uppercase of the slug by invariant. Same machine, every surface.
    if (!out.slug) {
      const s2 = getSlug();
      if (s2) {
        out.slug = s2;
        if (!out.zid) out.zid = s2.toUpperCase();
      }
    }
    return out;
  } catch { return {}; }
}
